/**
 * Runtime config, so one build artifact runs in every environment.
 *
 * `settings-default.json` (beside this file) is bundled and holds the prod
 * values. A `settings-override.json` placed next to the deployed index.html
 * overrides individual keys — that file is the environment, it is never
 * committed (Sagi, 2026-10-06). Merge is shallow: every key is a flat string.
 *
 * `settings` is empty until `loadSettings()` resolves. main.tsx awaits it
 * BEFORE dynamically importing App, so module-scope reads anywhere under App
 * see the loaded values. Node-run checks never call it and read undefined,
 * exactly as they read `import.meta.env` before this existed.
 */
import defaults from "./settings-default.json";

export type Settings = typeof defaults;

export const settings = {} as Settings;

export async function loadSettings(): Promise<void> {
  let override: Record<string, unknown> = {};
  // Relative path: the app is served under a versioned sub-path. A missing
  // file comes back as the host's / Vite's index.html with a 200, so anything
  // that is not a JSON object means "no override", not an error.
  const res = await fetch("settings-override.json", { cache: "no-store" });
  if (res.ok) {
    const parsed: unknown = await res.json().catch(() => null);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      override = parsed as Record<string, unknown>;
    }
  }

  const unknown = Object.keys(override).filter((k) => !(k in defaults));
  if (unknown.length > 0) {
    console.warn(`[settings] unknown override keys (typo?): ${unknown.join(", ")}`);
  }

  Object.assign(settings, defaults, override);

  const bad = Object.keys(defaults).filter(
    (k) => typeof settings[k as keyof Settings] !== "string" || !settings[k as keyof Settings],
  );
  if (bad.length > 0) {
    throw new Error(`settings-override.json: empty or non-string values for ${bad.join(", ")}`);
  }
  if (Object.keys(override).length > 0) console.info("[settings] override", override);
}
